import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Table,
  Tbody,
  Tr,
  Th,
  Thead,
  Td,
  Text,
  Badge,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { getAuditDiffs, isLegacyAuditLog } from 'utils/auditFormat';

const ACTION_COLORS = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  CALCULATE: 'cyan',
  CALCULATE_PAYROLL: 'cyan',
  MARK: 'purple',
  APPROVE: 'teal',
  CANCEL: 'orange',
  TERMINATE: 'orange',
};

export default function AuditChangesModal({ isOpen, onClose, log }) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const beforeBg = useColorModeValue('red.50', 'whiteAlpha.100');
  const afterBg = useColorModeValue('green.50', 'whiteAlpha.100');
  const theadBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  if (!log) return null;

  const diffs = getAuditDiffs(log);
  const legacy = isLegacyAuditLog(log);
  const hasBefore = diffs.some((d) => d.before && d.before !== '—');

  const formatDate = (raw) => {
    if (!raw) return '—';
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString('es-UY');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          Cambios realizados
          <Badge ml={2} colorScheme={ACTION_COLORS[log.action] || 'gray'} fontSize="xs">
            {log.action}
          </Badge>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={0}>
          <Box mb={4}>
            <Text fontSize="sm" color={muted}>
              {log.userName || (log.userId ? `Usuario #${log.userId}` : 'Sistema')}
              {' · '}
              {formatDate(log.createdDate)}
            </Text>
            <Text fontSize="xs" color={muted} mt={0.5}>
              {log.module}
              {log.entityId ? ` #${log.entityId}` : ''}
            </Text>
          </Box>

          {legacy ? (
            <Text fontSize="sm" color={muted}>
              Este registro se guardó antes de activar la comparación de valores. Editá
              nuevamente el dato para ver el valor anterior y el nuevo.
            </Text>
          ) : diffs.length === 0 ? (
            <Text fontSize="sm" color={muted}>
              No hay detalle de cambios para este registro.
            </Text>
          ) : (
            <Box overflowX="auto" borderWidth="1px" borderColor={borderColor} borderRadius="md">
              <Table size="sm" variant="simple">
                <Thead bg={theadBg}>
                  <Tr>
                    <Th borderColor={borderColor}>Campo</Th>
                    {hasBefore && <Th borderColor={borderColor}>Antes</Th>}
                    <Th borderColor={borderColor}>{hasBefore ? 'Después' : 'Valor'}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {diffs.map((diff) => (
                    <Tr key={diff.key}>
                      <Td borderColor={borderColor} fontWeight="600" whiteSpace="nowrap">
                        {diff.label}
                      </Td>
                      {hasBefore && (
                        <Td borderColor={borderColor} bg={beforeBg} fontSize="sm">
                          {diff.before && diff.before !== '—' ? diff.before : '—'}
                        </Td>
                      )}
                      <Td borderColor={borderColor} bg={hasBefore ? afterBg : undefined} fontSize="sm">
                        {diff.after}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
